import { useState, useCallback } from 'react';
import {
  BUG_REPO_ALLOWLIST,
  type BugFileResponse,
} from '@volley/crucible-ipc-types/ipc-channels';
import { unflattenIpcError } from '@volley/crucible-ipc-types/error-types';
import { useBugDraft, useFileBug, useCancelBug } from '../hooks/useBugDraft';

/**
 * Issue #246 — `<BugDraftCard>` is the renderer's half of the
 * file-based draft contract for the `/file-bug` SKILL. The SKILL
 * writes `<gameDir>/.foundry/bug-draft.json` from chat context;
 * this card watches the file via `useBugDraft` polling and surfaces
 * the assembled title + body for user review. Send POSTs via the
 * `bug:file` IPC; Cancel deletes the draft via `bug:cancel`.
 *
 * v1 keeps body read-only on purpose — the brief defers "Edit
 * first" to v2 (the SKILL will accept paste-back corrections in
 * chat). The repo dropdown lets the user override the SKILL's
 * recommended target within the hardcoded allowlist.
 *
 * Mount point: in the chat panel above the input area, alongside
 * `<ReviewAnswersButton>`. The card auto-unmounts when
 * `useBugDraft` returns null (file gone — success path or cancel).
 */
export interface BugDraftCardProps {
  gameId: string;
  /**
   * Optional callback when an issue is filed. Parent uses this to
   * append a chat-bubble carrying the live URL so the user can
   * click through. Wired this way (rather than the card opening
   * the URL itself) so the chat history retains the link.
   */
  onFiled?: (result: { url: string; number: number; repo: string }) => void;
}

export function BugDraftCard({ gameId, onFiled }: BugDraftCardProps): React.JSX.Element | null {
  const { data: draft } = useBugDraft(gameId);
  const { fileBug } = useFileBug();
  const { cancelBug } = useCancelBug();

  // Selected repo defaults to whatever the SKILL recommended. The
  // user can switch within the allowlist before clicking Send.
  // We stage the selection in local state so a re-poll that
  // re-fetches the draft (which it will, every 2s) doesn't blow
  // away the user's manual override.
  const [selectedRepo, setSelectedRepo] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [errorState, setErrorState] = useState<BugFileResponse | null>(null);
  // After a successful POST, main deletes the draft file and the
  // next poll returns null — that would unmount the card before the
  // user sees the URL. Capture success locally so the card can
  // render a brief confirmation banner with the link until the user
  // dismisses it (or navigates away).
  const [filed, setFiled] = useState<{ url: string; number: number; repo: string } | null>(null);

  const repo = selectedRepo ?? draft?.repo ?? BUG_REPO_ALLOWLIST[0]!;

  const handleSend = useCallback(async () => {
    if (!draft) return;
    setPending(true);
    setErrorState(null);
    try {
      // Pass the draft's `draftedAt` to main as a TOCTOU guard. If
      // the SKILL overwrote the file between the last poll and this
      // click, main refuses with `errorKind: 'draft-changed'` and
      // the next poll-tick re-paints with the new draft body for
      // the user to review.
      const result = await fileBug({ gameId, repo, draftedAt: draft.draftedAt });
      if (result.ok && result.url && typeof result.number === 'number') {
        const success = { url: result.url, number: result.number, repo };
        setFiled(success);
        onFiled?.(success);
      } else {
        setErrorState(result);
      }
    } catch (err) {
      // IPC-layer rejection (e.g. main throws before producing a
      // structured `BugFileResponse` — `readStoredTokens()` raising
      // `EncryptionUnavailableError`, an fs error in `readBugDraft()`,
      // a preload-bridge failure). The IPC adapter rethrows a
      // flattened `[CRUCIBLE_ERR_V1]` envelope, so we run it through
      // `unflattenIpcError` first to extract the user-facing message
      // before populating the `unexpected` response — otherwise the
      // card would render the raw envelope. Same pattern as
      // `OnboardingOverlay.handleSaveAndVerify`.
      const decoded = unflattenIpcError(err);
      setErrorState({
        ok: false,
        errorKind: 'unexpected',
        error: decoded.message || 'Something went wrong filing the bug.',
      });
    } finally {
      setPending(false);
    }
  }, [draft, fileBug, gameId, repo, onFiled]);

  const handleCancel = useCallback(async () => {
    setPending(true);
    setErrorState(null);
    try {
      const result = await cancelBug(gameId);
      // Main returns `{ ok: false }` if it can't unlink the draft
      // (Windows file lock, permission denial, etc.) rather than
      // rejecting. Without this branch the card would clear the
      // error, refetch, see the still-present draft, and silently
      // re-mount — leaving the user with no explanation for why
      // Cancel "did nothing".
      if (!result.ok) {
        setErrorState({
          ok: false,
          errorKind: 'unexpected',
          error:
            'Could not delete the draft. The file may be locked or read-only — close any editor that has `bug-draft.json` open and try again.',
        });
      }
    } catch (err) {
      // Same rationale as `handleSend` — IPC rejections shouldn't
      // wedge the card silently. Decode the flattened envelope so
      // the user sees the structured message rather than the raw
      // `[CRUCIBLE_ERR_V1]` wrapper text.
      const decoded = unflattenIpcError(err);
      setErrorState({
        ok: false,
        errorKind: 'unexpected',
        error: decoded.message || 'Something went wrong cancelling the draft.',
      });
    } finally {
      setPending(false);
    }
  }, [cancelBug, gameId]);

  // Success-banner branch: the file is now gone but we want to
  // confirm the issue url to the user. Stays mounted until the user
  // clicks Dismiss or navigates away from the workspace.
  if (filed) {
    return (
      <div
        data-testid="bug-draft-card-filed"
        className="mb-3 rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-4"
      >
        <div className="mb-2 text-xs font-medium uppercase tracking-wider text-emerald-300/80">
          Bug filed
        </div>
        <div className="mb-3 text-sm text-white">
          Issue #{filed.number} created in{' '}
          <span className="font-mono text-xs text-white/70">{filed.repo}</span>.
        </div>
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            data-testid="bug-draft-dismiss"
            onClick={() => setFiled(null)}
            className="rounded border border-white/15 px-3 py-1 text-xs text-white/70 transition-colors hover:bg-white/5"
          >
            Dismiss
          </button>
          <a
            data-testid="bug-draft-open-issue"
            href={filed.url}
            target="_blank"
            rel="noreferrer"
            className="rounded border border-emerald-500/40 bg-emerald-500/20 px-3 py-1 text-xs font-medium text-emerald-100 transition-colors hover:bg-emerald-500/30"
          >
            Open issue
          </a>
        </div>
      </div>
    );
  }

  if (!draft) return null;

  return (
    <div
      data-testid="bug-draft-card"
      className="mb-3 rounded-lg border border-amber-500/30 bg-amber-500/5 p-4"
    >
      <div className="mb-2 flex items-center gap-2">
        <span className="text-xs font-medium uppercase tracking-wider text-amber-300/80">
          Bug report draft
        </span>
        <span className="text-[10px] text-white/30">
          assembled by /file-bug · review before sending
        </span>
      </div>

      <div
        className="mb-2 text-sm font-medium text-white"
        data-testid="bug-draft-title"
      >
        {draft.title}
      </div>

      <pre
        className="mb-3 max-h-56 overflow-auto whitespace-pre-wrap break-words rounded border border-white/10 bg-black/30 p-2 font-mono text-[11px] text-white/70"
        data-testid="bug-draft-body"
      >
        {draft.body}
      </pre>

      <div className="mb-3 flex items-center gap-2">
        <label className="text-xs text-white/60" htmlFor="bug-draft-repo">
          Target repo:
        </label>
        <select
          id="bug-draft-repo"
          data-testid="bug-draft-repo-select"
          value={repo}
          disabled={pending}
          onChange={(e) => setSelectedRepo(e.currentTarget.value)}
          className="rounded border border-white/10 bg-black/30 px-2 py-1 text-xs text-white"
        >
          {BUG_REPO_ALLOWLIST.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
      </div>

      {errorState && !errorState.ok && (
        <div
          data-testid="bug-draft-error"
          className="mb-3 rounded border border-red-500/40 bg-red-500/10 p-2 text-xs text-red-200"
        >
          {errorState.error ?? 'Something went wrong filing the bug.'}
        </div>
      )}

      <div className="flex items-center justify-end gap-2">
        <button
          type="button"
          data-testid="bug-draft-cancel"
          onClick={() => {
            void handleCancel();
          }}
          disabled={pending}
          className="rounded border border-white/15 px-3 py-1 text-xs text-white/70 transition-colors hover:bg-white/5 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="button"
          data-testid="bug-draft-send"
          onClick={() => {
            void handleSend();
          }}
          disabled={pending}
          className="rounded border border-amber-500/40 bg-amber-500/20 px-3 py-1 text-xs font-medium text-amber-100 transition-colors hover:bg-amber-500/30 disabled:opacity-50"
        >
          {pending ? 'Filing…' : 'Send'}
        </button>
      </div>
    </div>
  );
}
